import { useState } from "react";
import { Link2, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { run, useLoad } from "../lib/util";
import { Badge, Btn, Card, Table } from "../components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useSchool } from "./ctx";

const SRC = { gestor: ["definido pelo gestor", "green"], anterior: ["correção de mês anterior", "green"], ia: ["automático (IA)", "info"], nome: ["automático (nome)", "info"], nenhum: ["fora do cardápio", "muted"] };

function EditLinks({ row, preps, month, onClose, onDone }) {
  const [sel, setSel] = useState(new Set(row.preparations));
  const [q, setQ] = useState("");
  const toggle = (p) => { const s = new Set(sel); s.has(p) ? s.delete(p) : s.add(p); setSel(s); };
  const save = async (body, msg) => { if (await run(() => api.put("/s/menu-links", { month, product_id: row.product_id, ...body }), msg)) onDone(); };
  const list = preps.filter((p) => p.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] rounded-xl" data-testid="menu-links-dialog">
        <DialogHeader><DialogTitle>Em quais preparos entra {row.name}?</DialogTitle></DialogHeader>
        <input data-testid="menu-links-search" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Buscar preparo…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-80 overflow-auto space-y-1 text-sm">
          {list.map((p, i) => (
            <label key={p} className="flex items-start gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" data-testid={`menu-links-opt-${i}`} checked={sel.has(p)} onChange={() => toggle(p)} className="mt-1" />
              <span>{p}</span>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {row.manual && <Btn variant="outline" data-testid="menu-links-reset" onClick={() => save({ preparations: null }, "Voltou à ligação automática")}>Voltar ao automático</Btn>}
          <Btn data-testid="menu-links-save" onClick={() => save({ preparations: [...sel] }, "Ligações salvas — controle diário recalculado")}>Salvar ({sel.size})</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MenuLinks() {
  const { month, tick, bump } = useSchool();
  const [data, reload] = useLoad(async () => (await api.get("/s/menu-links", { params: { month } })).data, [month, tick]);
  const [edit, setEdit] = useState(null);
  if (!data?.products?.length) return null;
  return (
    <Card data-testid="menu-links">
      <h3 className="font-heading font-bold text-lg text-[#0B192C] flex items-center gap-2 mb-1"><Link2 className="w-5 h-5" />Ligações dos produtos com o cardápio SEEDUC</h3>
      <p className="text-xs text-slate-500 mb-3">Cada produto é distribuído nos dias dos preparos ligados a ele, na proporção de quantas vezes aparece. Sem ligação, sai a partir da emissão da nota (agricultura familiar: em todos os dias letivos do mês). Corrija se alguma ligação estiver errada; a correção vale também para os próximos meses em que o preparo se repetir.</p>
      <Table testid="menu-links-table">
        <thead><tr><th>Produto</th><th>Preparos ligados</th><th className="num">Vezes no mês</th><th>Origem</th><th></th></tr></thead>
        <tbody>
          {data.products.map((r) => (
            <tr key={r.product_id} data-testid={`menu-link-row-${r.product_id}`}>
              <td className="font-semibold">{r.name}</td>
              <td className="text-xs max-w-[420px]">{r.preparations.length ? r.preparations.join(" · ") : <span className="text-slate-400">{["gestor", "anterior"].includes(r.source) ? "fora do cardápio (sai a partir da nota)" : "—"}</span>}</td>
              <td className="num">{r.times || "—"}</td>
              <td><Badge tone={SRC[r.source][1]}>{SRC[r.source][0]}</Badge>{r.inherited_from && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`menu-link-from-${r.product_id}`}>de {r.inherited_from.slice(5)}/{r.inherited_from.slice(0, 4)}</div>}</td>
              <td><Btn variant="outline" className="!h-8 !px-2" data-testid={`menu-link-edit-${r.product_id}`} onClick={() => setEdit(r)}><Pencil className="w-3.5 h-3.5" />Corrigir</Btn></td>
            </tr>
          ))}
        </tbody>
      </Table>
      {edit && <EditLinks row={edit} preps={data.preparations} month={month} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); bump(); }} />}
    </Card>
  );
}
